/**
 * Phase 3C: Op Note Viewer Component
 * 
 * Read-only display of the immutable operative note
 * with "Replace Note" action for authorized users.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileText, 
  Lock, 
  RefreshCw, 
  Clock, 
  User,
  Eye,
} from 'lucide-react';
import { format } from 'date-fns';
import { OpNoteSourceType, OP_NOTE_SOURCE_LABELS } from '@/lib/phase3/types';

interface OpNoteViewerProps {
  rawText: string;
  sourceType: OpNoteSourceType;
  uploadedAt: string;
  uploadedBy?: string;
  onReplaceNote?: () => void;
  canReplace?: boolean;
  isDemoMode?: boolean;
}

export function OpNoteViewer({
  rawText,
  sourceType,
  uploadedAt,
  uploadedBy,
  onReplaceNote,
  canReplace = false,
  isDemoMode = false,
}: OpNoteViewerProps) {
  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Original Operative Note</CardTitle>
            <Badge variant="secondary" className="gap-1">
              <Lock className="h-3 w-3" />
              Read-Only
            </Badge>
          </div>
          {canReplace && onReplaceNote && (
            <Button
              variant="outline"
              size="sm"
              onClick={onReplaceNote}
              className="gap-1"
            >
              <RefreshCw className="h-4 w-4" />
              Replace Note
            </Button>
          )}
        </div>
        
        {/* Metadata */}
        <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            <span>Source: {OP_NOTE_SOURCE_LABELS[sourceType]}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>Uploaded: {format(new Date(uploadedAt), 'MMM d, yyyy h:mm a')}</span>
          </div>
          {uploadedBy && (
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              <span>By: {isDemoMode ? 'Demo User' : uploadedBy}</span>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        {isDemoMode && (
          <div className="mb-3 p-2 bg-warning/10 border border-warning/20 rounded-md text-sm text-warning">
            Demo Mode — This is simulated operative note text for illustration only.
          </div>
        )}
        
        <ScrollArea className="h-[400px] rounded-md border bg-muted/30 p-4">
          <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed">
            {rawText}
          </pre>
        </ScrollArea>
        
        <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
          <Lock className="h-3 w-3" />
          <span>
            This document is immutable. Any replacement will be logged with full audit trail.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
